import React, { useState } from "react"
import { Box, Typography, Divider, Collapse } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import { formatLatLngDM } from "@/utils/geoFormat"
import {
  collectHraWarningItems,
  collectJwlaWarningItems,
  collectCustomAreaWarningItems,
  collectEcaInfoItems,
  uncheckedLegInfo,
} from "@/utils/routeHelper"
import { useAccessContext } from "@/contexts/AccessContext"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
dayjs.extend(utc)

const LABEL_SX = { fontSize: 11, color: "#6b7280" }
const VALUE_SX = { fontSize: 11, color: "#004f7d", fontWeight: 600 }
const ROW_SX = { display: "flex", justifyContent: "space-between", mb: 0.35 }
const WARNING_LABEL_SX = { fontSize: 11, color: "#5f2120" }
const INFO_LABEL_SX = { fontSize: 11, color: "#663c00" }

function Row({ label, value }) {
  return (
    <Box sx={ROW_SX}>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Typography sx={VALUE_SX}>{value ?? "N/A"}</Typography>
    </Box>
  )
}

function InfoSection({ title, items }) {
  return (
    <Box
      sx={{
        mx: 1,
        mb: 0.75,
        px: 1,
        py: 0.5,
        background: "rgba(255,244,229,0.95)",
        border: "1px solid rgba(237,108,2,0.2)",
        borderRadius: 1,
      }}
    >
      <Typography
        sx={{ fontSize: 11, fontWeight: 700, color: "#ed6c02", mb: 0.25 }}
      >
        {title}
      </Typography>
      {items.map((item, i) => (
        <Box key={i} sx={{ ...ROW_SX, mb: 0.2 }}>
          <Typography sx={INFO_LABEL_SX}>• {item}</Typography>
        </Box>
      ))}
    </Box>
  )
}

function WarningRow({ text }) {
  return (
    <Box sx={{ ...ROW_SX, mb: 0.2 }}>
      <Typography sx={WARNING_LABEL_SX}>• {text}</Typography>
    </Box>
  )
}

function formatNum(val, decimals = 1, unit = "") {
  if (val == null) return null
  const n = Number(val)
  if (!Number.isFinite(n)) return null
  return `${n.toFixed(decimals)}${unit ? ` ${unit}` : ""}`
}

// hideEmpty: drop rows without data (uncomputed sketch points).
export default function WaypointCardContent({
  feature,
  routeType,
  hideEmpty = false,
}) {
  const [detailsExpanded, setDetailsExpanded] = useState(false)
  const { accessContext } = useAccessContext()
  // Same gate that controls RNW Analyser visibility — only users with the
  // `visibility_rnw_analyser=true` access flag get the raw property dump.
  const canSeeRawDetails = !!accessContext?.uiCapabilities?.canSeeRnwAnalyser
  const props = feature?.properties ?? {}
  const coords = feature?.geometry?.coordinates

  // Timestamp and coordinates
  const timestamp = props.timestamp
    ? dayjs.utc(props.timestamp).format("DD MMM YYYY HH:mm [UTC]")
    : "N/A"
  // Display normalises lng to [-180, 180] (popup never shows e.g. 246° W).
  const coordStr = coords ? formatLatLngDM(coords[0], coords[1]) : "N/A"

  // SOG, Heading (sensor) and COG (bearing to the next waypoint)
  const sog =
    props.sog ??
    props.albis_sensors?.speedOverGround_kn ??
    props.albis_reports?.speedOverGround_kn
  const heading = props.heading ?? props.albis_sensors?.headingTrue_deg
  const cog = props.course_bearing
  // Only reported waypoints have a heading sensor; planned ones only have a course.
  const showHeading = routeType === "reported"

  // Weather — snapshot → flat fallback
  const ws = props.weather_snapshot ?? {}

  const hssw =
    ws.wave_nww3_significant_height_m_interp ??
    ws.wave_swh ??
    props.hssw
  const dmean =
    ws.wave_nww3_mean_direction_deg_interp ??
    ws.wave_dirpw ??
    props.dmean

  const windSpeed = ws.wind_gfs_speed_knots_interp ?? ws.wind_speed_knots
  const windDir = ws.wind_gfs_direction_deg_interp ?? ws.wind_direction_deg
  const u10 = props.u10
  const v10 = props.v10

  const curSpeed = ws.current_hycom_speed_knots_interp ?? ws.current_speed_knots
  const curDir =
    ws.current_hycom_direction_deg_interp ?? ws.current_direction_deg
  const ucur = props.ucur
  const vcur = props.vcur

  // Compute wind from U/V if no pre-computed speed
  let windDisplay = null
  if (windSpeed != null) {
    windDisplay = formatNum(windSpeed, 2, "kn")
    if (windDir != null) windDisplay += ` / ${Math.round(windDir)}°`
  } else if (Number.isFinite(u10) && Number.isFinite(v10)) {
    const speed = Math.sqrt(u10 * u10 + v10 * v10)
    const angleRad = Math.atan2(v10, u10)
    let angleDeg = (270 - (angleRad * 180) / Math.PI) % 360
    if (angleDeg < 0) angleDeg += 360
    windDisplay = `${speed.toFixed(2)} kn / ${Math.round(angleDeg)}°`
  }

  // Compute current from U/V if no pre-computed speed
  let currentDisplay = null
  if (curSpeed != null) {
    currentDisplay = formatNum(curSpeed, 2, "kn")
    if (curDir != null) currentDisplay += ` / ${Math.round(curDir)}°`
  } else if (Number.isFinite(ucur) && Number.isFinite(vcur)) {
    const speed = Math.sqrt(ucur * ucur + vcur * vcur)
    const angleRad = Math.atan2(vcur, ucur)
    let angleDeg = (270 - (angleRad * 180) / Math.PI) % 360
    if (angleDeg < 0) angleDeg += 360
    currentDisplay = `${speed.toFixed(2)} kn / ${Math.round(angleDeg)}°`
  }

  // Wave display
  let waveDisplay = null
  if (hssw != null || dmean != null) {
    const parts = []
    if (hssw != null) parts.push(formatNum(hssw, 2, "m"))
    if (dmean != null) parts.push(`${Math.round(dmean)}°`)
    waveDisplay = parts.filter(Boolean).join(" / ")
  }

  const hasWeather = waveDisplay || windDisplay || currentDisplay

  // Warnings
  const warnings = props.warnings
  const rnw = props.rnw_evaluation
  const warningItems = []

  if (warnings && typeof warnings === "object") {
    if (warnings.warning_segment_wind_speed_knots)
      warningItems.push(
        `Wind speed: ${formatNum(warnings.warning_segment_wind_speed_knots, 1, "kn")}`,
      )
    if (warnings.warning_segment_wave_height_meter)
      warningItems.push(
        `Wave height: ${formatNum(warnings.warning_segment_wave_height_meter, 1, "m")}`,
      )
    if (warnings.warning_segment_current_speed_knots)
      warningItems.push(
        `Current speed: ${formatNum(warnings.warning_segment_current_speed_knots, 1, "kn")}`,
      )
    if (warnings.warning_is_closed_viaarea === true)
      warningItems.push("Route passes through a closed via area")
    if (warnings.warning_leg_usable === true) {
      const rule = props.restrictions?.ww_restriction_text_check
      warningItems.push("Leg not usable" + (rule ? `: ${rule}` : ""))
    }
    if (warnings.warning_is_free_usage === true) {
      const usage = props.leg_data?.usage
      if (usage === 2) {
        warningItems.push("One-way leg")
      } else {
        warningItems.push(
          "Usage restriction" + (usage != null ? ` (usage: ${usage})` : ""),
        )
      }
    }
    if (warnings.warning_vertical_clearance === true) {
      const vc = props.leg_data?.vertical_clearance
      warningItems.push(
        "Insufficient vertical clearance" +
          (vc != null ? `: ${formatNum(vc, 1, "m")}` : ""),
      )
    }
    if (warnings.warning_water_depth === true) {
      const navCat = rnw?.ww_nav_cat
      warningItems.push(
        "Insufficient water depth" +
          (navCat != null ? ` (nav cat: ${navCat})` : ""),
      )
    }
    warningItems.push(...collectHraWarningItems(warnings))
    warningItems.push(...collectJwlaWarningItems(warnings))
    warningItems.push(...collectCustomAreaWarningItems(warnings))
  }

  const infoItems = []
  if (rnw && typeof rnw === "object") {
    if (rnw.ww_maxspeed_knots != null)
      infoItems.push(`Max speed: ${formatNum(rnw.ww_maxspeed_knots, 1, "kn")}`)
    if (rnw.ww_minspeed_knots != null)
      infoItems.push(`Min speed: ${formatNum(rnw.ww_minspeed_knots, 1, "kn")}`)
  }
  const uncheckedLeg = uncheckedLegInfo(warnings)
  const zoneItems = collectEcaInfoItems(warnings)

  // ALBIS details (reported routes)
  const albisData = props.albis_sensors ?? props.albis_reports
  const hasAlbisDetails = !!albisData

  return (
    <>
      {/* Header info */}
      <Box sx={{ px: 1, py: 0.75 }}>
        {props.name && (
          <Box sx={ROW_SX}>
            <Typography sx={LABEL_SX}>WP</Typography>
            <Typography sx={VALUE_SX}>{props.name}</Typography>
          </Box>
        )}
        {hideEmpty && !props.timestamp ? null : (
          <Box sx={ROW_SX}>
            <Typography sx={LABEL_SX}>Time</Typography>
            <Typography sx={VALUE_SX}>{timestamp}</Typography>
          </Box>
        )}
        <Box sx={ROW_SX}>
          <Typography sx={LABEL_SX}>Position</Typography>
          <Typography sx={{ ...VALUE_SX, fontSize: 10 }}>{coordStr}</Typography>
        </Box>

        <Divider sx={{ my: 0.5 }} />

        {/* Navigation */}
        {hideEmpty && sog == null ? null : (
          <Row label="SOG" value={formatNum(sog, 1, "kn")} />
        )}
        {showHeading && (
          <Row
            label="Heading"
            value={heading != null ? `${Math.round(heading)}°` : null}
          />
        )}
        {hideEmpty && cog == null ? null : (
          <Row label="COG" value={cog != null ? `${Math.round(cog)}°` : null} />
        )}

        {/* Weather */}
        {hideEmpty && !hasWeather ? null : (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Row label="Wave" value={waveDisplay} />
            <Row label="Wind" value={windDisplay} />
            <Row label="Current" value={currentDisplay} />
          </>
        )}
      </Box>

      {/* Warnings */}
      {warningItems.length > 0 && (
        <Box
          sx={{
            mx: 1,
            mb: 0.75,
            px: 1,
            py: 0.5,
            background: "rgba(253,237,237,0.95)",
            border: "1px solid rgba(211,47,47,0.2)",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{ fontSize: 11, fontWeight: 700, color: "#d32f2f", mb: 0.25 }}
          >
            Warnings
          </Typography>
          {warningItems.map((item, i) => (
            <WarningRow key={i} text={item} />
          ))}
        </Box>
      )}

      {infoItems.length > 0 && (
        <InfoSection title="Speed Limits" items={infoItems} />
      )}
      {uncheckedLeg && <InfoSection title="Leg" items={[uncheckedLeg]} />}
      {zoneItems.length > 0 && <InfoSection title="Zones" items={zoneItems} />}

      {/* Raw properties dump — only available to users with the RNW Analyser
          access flag. Ardmore (and other operational users) don't see it. */}
      {canSeeRawDetails && (
        <>
          <Box
            onClick={() => setDetailsExpanded((prev) => !prev)}
            sx={{
              px: 1,
              py: 0.5,
              borderTop: "1px solid rgba(0,79,125,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              color: "#004f7d",
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { backgroundColor: "rgba(0,79,125,0.06)" },
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
              {detailsExpanded ? "Hide details" : "Show details"}
            </Typography>
            {detailsExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            )}
          </Box>
          <Collapse in={detailsExpanded}>
            <Divider />
            <Box
              sx={{
                px: 1,
                py: 0.75,
                maxHeight: 280,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,79,125,0.25)",
                  borderRadius: 3,
                },
              }}
            >
              {flattenWaypointProps(props).map(([key, value]) => (
                <Row key={key} label={key} value={value} />
              ))}
            </Box>
          </Collapse>
        </>
      )}
    </>
  )
}

// Flatten waypoint properties into [label, value] rows, recursing one level
// into nested objects so blobs like weather_snapshot / rnw_evaluation /
// warnings show up as `weather_snapshot.wind_gfs_speed_knots_interp` etc.
// Skips empty values, internal helpers and very large nested arrays.
function flattenWaypointProps(props) {
  if (!props || typeof props !== "object") return []
  const SKIP_KEYS = new Set([
    // already shown explicitly in the header section above the expander
    "timestamp",
    "datetime_utc",
    "name",
    "number",
    "extended_number",
    "waypoint_number",
    "waypoint_subnumber",
  ])
  const rows = []
  const push = (label, value) => {
    if (value === null || value === undefined || value === "") return
    if (Array.isArray(value)) {
      if (value.length === 0) return
      rows.push([label, value.join(", ")])
      return
    }
    if (typeof value === "object") return // handled by recursion
    if (typeof value === "number") {
      rows.push([
        label,
        Number.isInteger(value) ? String(value) : Number(value).toFixed(2),
      ])
      return
    }
    rows.push([label, String(value)])
  }

  for (const [key, value] of Object.entries(props)) {
    if (SKIP_KEYS.has(key)) continue
    if (value && typeof value === "object" && !Array.isArray(value)) {
      // Recurse one level into JSONB blobs
      for (const [subKey, subValue] of Object.entries(value)) {
        push(`${key}.${subKey}`, subValue)
      }
    } else {
      push(key, value)
    }
  }
  rows.sort(([a], [b]) => a.localeCompare(b))
  return rows
}
